import { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { authService } from '@providers/context/services/authService';
import { encrypt, encryptSpecialRights, decryptSpecialRights } from '@utils/encryption';
import { personService } from '@shared/services/personService';
import { registerSessionExpiredHandler } from '@shared/services/authSession';
import { setAdminBranchId, clearAdminBranchId, setEditReportTemplates as setEditReportTemplatesRedux, clearEditReportTemplates } from '@store/slices/authSlice';
const AuthContext = createContext();
const REFRESH_INTERVAL = 25 * 60 * 1000; // 25 minutes
// const REFRESH_INTERVAL = 10000; // 1 second debug mode

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [auth, setAuth] = useState(() => {
        const accessToken = Cookies.get('accessToken');
        const refreshToken = Cookies.get('refreshToken');
        const encryptedRights = Cookies.get('specialRights');
        
        return {
            accessToken,
            refreshToken,
            specialRights: encryptedRights ? decryptSpecialRights(encryptedRights) : []
        };
    });
    
    const [errorMessage, setErrorMessage] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(!!auth.accessToken);
    const [specialRights, setSpecialRights] = useState(auth.specialRights);
    const [userFullName, setUserFullName] = useState('');

    useEffect(() => {
        let refreshInterval;

        if (auth.accessToken && auth.refreshToken) {

            refreshInterval = setInterval(async () => {
                try {
                    const response = await authService.refresh(auth.refreshToken);

                    if (response.data) {
                        const tokens = {
                            'Access Token': response.data,
                            'Refresh Token': auth.refreshToken
                        };
                        await setTokens(tokens, true);
                    } else {
                        logoutSubmitHandler();
                    }
                } catch (error) {
                    if (error?.response?.status === 403) {
                        logoutSubmitHandler();
                    }
                }
            }, REFRESH_INTERVAL);

        } else {
            console.error('No tokens present, refresh cycle not started');
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [auth.accessToken, auth.refreshToken]);

    useEffect(() => {
        const fetchUserFullName = async () => {
            if (isAuthenticated) {
                try {
                    const fullName = await personService.getPersonFullName();
                    setUserFullName(fullName);
                } catch (error) {
                    console.error('Error fetching user full name:', error);
                }
            }
        };

        fetchUserFullName();
    }, [isAuthenticated]);

    const setTokens = async (tokens, keepExistingRefreshToken = false) => {
        const accessToken = tokens['Access Token'];
        const refreshToken = keepExistingRefreshToken ? auth.refreshToken : tokens['Refresh Token'];
        const isProduction = import.meta.env.MODE === 'production';
        const cookieOptions = {
            secure: isProduction,
            sameSite: 'Strict',
            path: '/'
        };

        if (accessToken) {
            Cookies.set('accessToken', accessToken, { ...cookieOptions, expires: 1/48 });
        }

        if (refreshToken && !keepExistingRefreshToken) {
            Cookies.set('refreshToken', refreshToken, { ...cookieOptions, expires: 1 });
        }

        try {
            const rightsResponse = await authService.specialRights();
            // Extract specialRights array from response (handle both array and object responses)
            const rights = Array.isArray(rightsResponse) 
                ? rightsResponse 
                : (rightsResponse?.specialRights && Array.isArray(rightsResponse.specialRights))
                    ? rightsResponse.specialRights
                    : [];
            // Extract and save adminBranchId to Redux
            const adminBranchId = rightsResponse?.adminBranchId;
            if (adminBranchId) {
                dispatch(setAdminBranchId(adminBranchId));
            }
            dispatch(setEditReportTemplatesRedux(Boolean(rightsResponse?.editReportTemplates)));
            const encryptedRights = encryptSpecialRights(rights);
            
            if (encryptedRights) {
                Cookies.set('specialRights', encryptedRights, { ...cookieOptions, expires: 1 });
            }

            setAuth({ accessToken, refreshToken, specialRights: rights });
            setSpecialRights(rights);
            setIsAuthenticated(true);
            return rights;
        } catch (error) {
            console.error('Error setting rights:', error);
            return [];
        }
    };

    const loginSubmitHandler = async (formData) => {
        try {
            if (!formData.password) {
                setErrorMessage('Password is required');
                return false;
            }

            const response = await authService.login({
                username: formData.username,
                password: formData.password
            });

            if (response.data && response.data['Access Token'] && response.data['Refresh Token']) {
                const accessToken = response.data['Access Token'];
                const refreshToken = response.data['Refresh Token'];
                const isProduction = import.meta.env.MODE === 'production';
                const cookieOptions = {
                    secure: isProduction,
                    sameSite: 'Strict',
                    path: '/'
                };

                Cookies.set('accessToken', accessToken, { ...cookieOptions, expires: 1/48 });
                Cookies.set('refreshToken', refreshToken, { ...cookieOptions, expires: 1 });
                
                const rightsResponse = await authService.specialRights();
                // Extract specialRights array from response (handle both array and object responses)
                const rights = Array.isArray(rightsResponse) 
                    ? rightsResponse 
                    : (rightsResponse?.specialRights && Array.isArray(rightsResponse.specialRights))
                        ? rightsResponse.specialRights
                        : [];
                // Extract and save adminBranchId to Redux
                const adminBranchId = rightsResponse?.adminBranchId;
                if (adminBranchId) {
                    dispatch(setAdminBranchId(adminBranchId));
                }
                dispatch(setEditReportTemplatesRedux(Boolean(rightsResponse?.editReportTemplates)));
                const encryptedRights = encryptSpecialRights(rights);
                
                if (encryptedRights) {
                    Cookies.set('specialRights', encryptedRights, { ...cookieOptions, expires: 1 });
                }
                
                setAuth({ accessToken, refreshToken, specialRights: rights });
                setSpecialRights(rights);
                setIsAuthenticated(true);
                setErrorMessage('');
                
                navigate('/home');
                return true;
            }
            
            setErrorMessage('Invalid login response');
            return false;
        } catch (error) {
            console.error('Login error:', error);
            setErrorMessage(error.response?.data?.message || 'Login failed');
            return false;
        }
    };

    const logoutSubmitHandler = () => {
        Cookies.remove('accessToken', { path: '/' });
        Cookies.remove('refreshToken', { path: '/' });
        Cookies.remove('specialRights', { path: '/' });
        setAuth({});
        setSpecialRights([]);
        dispatch(clearAdminBranchId());
        dispatch(clearEditReportTemplates());
        setIsAuthenticated(false);
        navigate('/login', { state: { from: 'logout' } });
    };

    useEffect(() => {
        registerSessionExpiredHandler(logoutSubmitHandler);
    }, []);

    const values = {
        loginSubmitHandler,
        logoutSubmitHandler,
        isAuthenticated,
        auth,
        errorMessage,
        specialRights,
        userFullName,
    };

    return (
        <AuthContext.Provider value={values}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;